<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.itineraries', 2),
                        href: meta.links.itineraries_index,
                    },
                    {
                        name: itinerary.name,
                        href: meta.links.itineraries_show,
                    },
                    {
                        name: $tc('messages.itinerary_stations', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <NavLinks :links="meta.navGroupLinks" />
        </div>

        <AppCard class="mt-1">
            <div class="flex justify-between items-center px-2">
                <div class="flex items-center gap-x-2 mt-1">
                    <StationBase :color="itinerary.base_color" :shape="itinerary.base_shape" class="w-12 h-12">
                        <StationIcon :type="station.type" class="w-1/2 h-1/2 text-white opacity-50" />
                    </StationBase>

                    <span class="font-mono text-sm text-gray-400 tabular-nums">
                        {{ station.index }}
                    </span>

                    <span>{{ station.display_name }}</span>

                    <span class="text-sm text-gray-400" v-if="station.unlocked_at_display">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4 text-gray-400 inline-block icon icon-tabler icon-tabler-lock"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <rect x="5" y="11" width="14" height="10" rx="2"></rect>
                            <circle cx="12" cy="16" r="1"></circle>
                            <path d="M8 11v-4a4 4 0 0 1 8 0v4"></path>
                        </svg>

                        {{ station.unlocked_at_display }}</span
                    >
                </div>
            </div>
        </AppCard>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.started_at') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.completed_at') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in station.students"
                                :key="student.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <InertiaLink :href="student.links.show" class="text-indigo-600">
                                        {{ student.full_name }}
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-mono text-right">
                                    <Tippy v-if="student.created_at">
                                        <button>
                                            <span
                                                class="m-1 inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                            />
                                        </button>

                                        <template #content>
                                            {{ student.created_at }}
                                        </template>
                                    </Tippy>
                                    <span
                                        v-else
                                        class="m-1 inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-gray-400"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-mono text-right">
                                    <Tippy v-if="student.completed_at">
                                        <button>
                                            <span
                                                class="m-1 inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                            />
                                        </button>

                                        <template #content>
                                            {{ student.completed_at }}
                                        </template>
                                    </Tippy>
                                    <span
                                        v-else
                                        class="m-1 inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-gray-400"
                                    />
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                ></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import StationBase from '@/components/Forms/StationBase.vue';
import AppCard from '@/Shared/AppCard.vue';
import StationIcon from '@/components/Forms/StationIcon.vue';
import { Tippy } from 'vue-tippy';
import NavLinks from '@/components/NavLinks.vue';

export default {
    components: {
        NavLinks,
        StationIcon,
        AppCard,
        StationBase,
        PanelPage,
        Tippy,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'station', 'itinerary', 'gamificationSet'],
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
};
</script>
